import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import regenerateToken from '../regenerateToken';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';

/**
 * Access Control Context
 * Manages access permissions for sidebar navigation items based on:
 * 1. Report Configuration flags (showAdditionalRecommendationsPage, dynamicCoverPage)
 * 2. Report Add-ons from credentials (reportAddons array)
 */

const AccessContext = createContext({
    accessConfig: {
        productRecommendations: false,
        vizappConfiguration: false,
        assetManagement: false,
    },
    loading: true,
    error: null,
    refreshAccessConfig: () => Promise.resolve(),
});

export const useAccessConfig = () => useContext(AccessContext);

export const AccessProvider = ({ children }) => {
    const [accessConfig, setAccessConfig] = useState(() => {
        const isAdmin = checkAdmin();
        return {
            productRecommendations: isAdmin,
            vizappConfiguration: isAdmin,
            assetManagement: isAdmin,
            colorConfiguration: isAdmin,
            versionManagement: isAdmin,
            profileContent: isAdmin,
            parameterContent: isAdmin,
        };
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAccessConfiguration = useCallback(async () => {
        try {
            // 1. Get report configuration from API
            const idToken = Cookies.get('idToken');

            // Validate token exists before making API call
            if (!idToken) {
                console.error('AccessContext: No authentication token found');
                setError('Authentication required');
                setLoading(false);
                return; // Bail out early if no token
            }

            // Use environment variable for API endpoint - MUST be configured
            const stateConfigUrl = process.env.REACT_APP_STATE_CONFIG_URL;

            if (!stateConfigUrl) {
                throw new Error('REACT_APP_STATE_CONFIG_URL environment variable is not configured. Please set it in your .env file.');
            }

            const stateConfigRequest = buildRequestConfig({
                method: 'GET',
                url: stateConfigUrl,
            });

            let stateConfigResponse = await axios(stateConfigRequest);

            // Handle token refresh if needed
            if (stateConfigResponse.status === 401) {
                if (process.env.NODE_ENV === 'development') {
                    console.log('AccessContext: Regenerating ID Token');
                }
                await regenerateToken();

                // Get the fresh token after regeneration
                const freshToken = Cookies.get('idToken');

                if (!freshToken) {
                    throw new Error('Token regeneration failed');
                }

                // Rebuild request to pick up fresh token (buildRequestConfig naturally reads latest cookie)
                const refreshedRequest = buildRequestConfig({
                    method: 'GET',
                    url: stateConfigUrl,
                });

                try {
                    stateConfigResponse = await axios(refreshedRequest);

                    // Validate the retry response
                    if (stateConfigResponse.status === 401) {
                        throw new Error('Authentication failed after token refresh');
                    }

                    if (stateConfigResponse.status < 200 || stateConfigResponse.status >= 300) {
                        throw new Error(`API request failed with status ${stateConfigResponse.status}`);
                    }
                } catch (retryError) {
                    console.error('AccessContext: Retry request failed', retryError);
                    throw new Error(`Failed to fetch configuration after token refresh: ${retryError.message}`);
                }
            }

            // Validate response data before using
            if (!stateConfigResponse?.data?.data) {
                console.warn('AccessContext: API response missing expected data structure');
            }

            const stateData = stateConfigResponse?.data?.data?.stateData || {};

            // 2. Get credentials from localStorage for reportAddons and new permission flags
            const storedCredentials = localStorage.getItem('credentials');
            let reportAddons = [];
            let personalisedServices;
            let customisation;

            if (storedCredentials) {
                try {
                    const parsed = JSON.parse(storedCredentials);
                    const rs = parsed.reportSubscription || {};
                    reportAddons = parsed.reportAddons || [];
                    // personalisedServices & customisation are nested inside reportSubscription
                    // Fallback to top-level for backward compatibility with older responses
                    personalisedServices = rs.personalisedServices || parsed.personalisedServices;
                    customisation = rs.customisation || parsed.customisation;
                } catch (e) {
                    console.error('AccessContext: Failed to parse credentials', e);
                }
            }

            const isAdmin = checkAdmin();

            // 3. Determine access based on configuration
            // Product Recommendations: Based on showAdditionalRecommendationsPage flag
            const hasProductRecommendationsAccess = stateData.showAdditionalRecommendationsPage === true;

            // Viz App Configuration Logic
            // Priority 1: Check personalisedServices.vizapp (New Client)
            // Priority 2: Fallback to reportAddons (Old Client / Backward Compatibility)
            let hasVizAppAccess = true; // Default allow

            if (personalisedServices && typeof personalisedServices.vizapp !== 'undefined') {
                // New Client: Use the explicit flag
                hasVizAppAccess = personalisedServices.vizapp === true;
            } else {
                // Old Client / Missing Flag: Check reportAddons or default to unlocked if implied by context
                // Keeping existing check for backward compatibility if needed, but per request:
                // "If that vizapp flag is not coming in that JSON, you can show always that wizard tab"
                hasVizAppAccess = true;
            }

            // Customization Capabilities Logic
            // Profile Content
            let hasProfileContentAccess = true; // Default allow (Old Client)
            if (customisation && customisation.content && typeof customisation.content.profile !== 'undefined') {
                hasProfileContentAccess = customisation.content.profile === true;
            }

            // Parameter Content
            let hasParameterContentAccess = true; // Default allow (Old Client)
            if (customisation && customisation.content && typeof customisation.content.parameter !== 'undefined') {
                hasParameterContentAccess = customisation.content.parameter === true;
            }

            // Color Configuration: Based on customisation.design
            // Old Client (no customisation data) → default: unlocked
            let hasColorConfigAccess = true;
            if (customisation && typeof customisation.design !== 'undefined') {
                hasColorConfigAccess = customisation.design === true;
            }

            // Version Management: Unlocked only if BOTH profile AND parameter content are accessible
            // Old Client → both default true → version management also defaults true
            const hasVersionManagementAccess = hasProfileContentAccess && hasParameterContentAccess;

            // Asset Management: Based on dynamicCoverPage or generateCoverPage flags
            // Unlocks if any of these tags are true
            // Note: Checking for both 'generateCoverPage' and 'generateCovePage' due to a typo in ReportConfiguration
            const hasAssetManagementAccess = stateData.dynamicCoverPage === true ||
                stateData.generateCoverPage === true ||
                stateData.generateCovePage === true;

            setAccessConfig({
                productRecommendations: isAdmin || hasProductRecommendationsAccess,
                vizappConfiguration: isAdmin || hasVizAppAccess,
                assetManagement: isAdmin || hasAssetManagementAccess,
                profileContent: isAdmin || hasProfileContentAccess,
                parameterContent: isAdmin || hasParameterContentAccess,
                colorConfiguration: isAdmin || hasColorConfigAccess,
                versionManagement: isAdmin || hasVersionManagementAccess,
            });

            // Only log in development to avoid exposing sensitive config
            if (process.env.NODE_ENV === 'development') {
                console.log('AccessContext: Configuration loaded', {
                    accessConfig: {
                        productRecommendations: hasProductRecommendationsAccess,
                        vizappConfiguration: hasVizAppAccess,
                        assetManagement: hasAssetManagementAccess,
                        profileContent: hasProfileContentAccess,
                        parameterContent: hasParameterContentAccess,
                        colorConfiguration: hasColorConfigAccess,
                        versionManagement: hasVersionManagementAccess,
                    },
                    flags: {
                        dynamicCoverPage: stateData.dynamicCoverPage,
                        generateCoverPage: stateData.generateCoverPage
                    }
                });
            }

            setLoading(false);
        } catch (err) {
            console.error('AccessContext: Error fetching configuration', err);

            // Critical: If we hit an error, we MUST still unlock the UI for Admins
            const isAdmin = checkAdmin();

            if (isAdmin) {
                setAccessConfig({
                    productRecommendations: true,
                    vizappConfiguration: true,
                    assetManagement: true,
                    profileContent: true,
                    parameterContent: true,
                    colorConfiguration: true,
                    versionManagement: true,
                });
            }

            setError(err.message);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAccessConfiguration();

        // Listen for admin client change events to re-fetch appropriately
        const handler = () => {
            setLoading(true);
            fetchAccessConfiguration();
        };

        window.addEventListener('adminClientChanged', handler);
        return () => window.removeEventListener('adminClientChanged', handler);
    }, [fetchAccessConfiguration]);

    return (
        <AccessContext.Provider value={{ accessConfig, loading, error, refreshAccessConfig: fetchAccessConfiguration }}>
            {children}
        </AccessContext.Provider>
    );
};

export default AccessContext;
